import { get, post, put, del } from "./api";
import type { IError } from "./api";
import { API } from "@/constants";
import { doAsHeaders } from "@/services/viewAsUser";
import { normalizeSpecOrThrow, servedSchemaVersion } from "@/utils/dashboardSpecV3Normalize";
import type { DashboardSpec } from "@/types/dashboard.types";
import type {
  CompanyEntitlements,
  DashboardRef,
  DashboardShareEntry,
  ShareDashboardRequest,
  UseCase,
  UseCaseDashboard,
  UseCaseRole,
  UserAccessTree,
} from "@/types/use-case.types";

/**
 * Use-Case API client. Backed by the `/use-cases/*` router in
 * `iq-backend@iq-dashboards` (Ajay's `CU-86d2hxuth`). The backend speaks
 * snake_case; this module is the single place that maps it to the camelCase
 * shapes the rest of the app consumes (mirrors `dashboardApi.ts` conventions).
 *
 * The consumer routes (`me`, single-dashboard) are open to any authenticated
 * user (the backend narrows by entitlement + share). The catalog/entitlement
 * routes are SarasAdmin-gated on the backend today — a DashboardCreator can only
 * call them once the backend widens `get_admin_user` (tracked prerequisite).
 */

// ── Raw (snake_case) API shapes — local to the mapping boundary ──────────────

interface RawDashboardRef {
  /** Opaque string on current backends; older backends still send an integer. */
  dashboard_id: string | number;
  title: string;
  order_index?: number;
}

interface RawUseCase {
  id: number;
  name: string;
  description?: string;
  enabled?: boolean;
  // Only the access-tree (`/me`) node carries dashboards; the flat catalog
  // (`UseCaseResponse`) omits them — we compose them in `fetchUseCases`.
  dashboards?: RawDashboardRef[];
}

interface RawUserAccessTree {
  use_cases?: RawUseCase[];
}

interface RawUseCaseDashboard {
  dashboard_id: string | number;
  title: string;
  use_case_id: number;
  spec?: DashboardSpec;
}

/** `GET /use-cases/roles` → bare `list[RoleResponse]` (`{role_id, name, role_name}`). */
interface RawUseCaseRole {
  role_id: number;
  name?: string;
  role_name?: string;
}

/** `GET/PUT .../entitlements` → `{company_id, entitlements: UseCaseResponse[]}`. */
interface RawCompanyEntitlements {
  company_id: number;
  entitlements?: RawUseCase[];
}

// ── Mappers ──────────────────────────────────────────────────────────────────

const toDashboardRef = (d: RawDashboardRef, i: number): DashboardRef => ({
  // Coerce: the route param is a string, and the view matches `dashboardId ===
  // :dashboardId`. A numeric id from an older backend would never match and the
  // view would redirect to itself forever (blank page).
  dashboardId: String(d.dashboard_id),
  title: d.title,
  // Fall back to array position when the backend omits an explicit order.
  orderIndex: d.order_index ?? i,
});

const toUseCase = (u: RawUseCase): UseCase => ({
  id: u.id,
  name: u.name,
  description: u.description,
  dashboards: (u.dashboards ?? [])
    .map(toDashboardRef)
    // Present in tab order regardless of the order the backend returns them.
    .sort((a, b) => a.orderIndex - b.orderIndex),
});

// ── Consumer endpoints (any authenticated user) ──────────────────────────────

/**
 * `GET /use-cases/me` → the current user's access tree (entitled use cases, each
 * narrowed to the dashboards shared with them). Drives the two-tier nav.
 */
export const fetchMyAccessTree = async (): Promise<UserAccessTree> => {
  // Optional feature endpoint — 404s on backends without the use-cases router.
  // The two-tier nav degrades to empty, so skip the global error toast for that
  // 404 only (a real 500 still surfaces).
  const res = await get<RawUserAccessTree>(API.USE_CASES.ME, {
    skipGlobalErrorHandler: [404],
  });
  return { useCases: (res?.use_cases ?? []).map(toUseCase) };
};

/**
 * `GET /use-cases/{useCaseId}/dashboards/{dashboardId}` → a single dashboard's
 * metadata + renderable `spec`, scoped to its use case (the backend enforces the
 * entitlement + share gate here).
 */
export const fetchUseCaseDashboard = async (
  useCaseId: number,
  dashboardId: string,
): Promise<UseCaseDashboard> => {
  // `UseCaseDashboardView` renders explicit inline states for a denied (403) or
  // missing (404) dashboard, surfacing the backend's own message — a global toast
  // on top would double-signal it. A genuine 5xx still surfaces globally.
  const res = await get<RawUseCaseDashboard>(API.USE_CASES.DASHBOARD(useCaseId, dashboardId), {
    skipGlobalErrorHandler: [403, 404],
    // Dashboard DATA read — opts into "View as user" (doAs), so an impersonated
    // view resolves the spec through the TARGET user's entitlements. `removeDashboard`
    // DELETEs this exact URL and deliberately does NOT opt in: a mutation must
    // always run as the admin, even when it shares a URL with a read.
    headers: doAsHeaders(),
  });
  return {
    dashboardId: String(res.dashboard_id),
    title: res.title,
    useCaseId: res.use_case_id,
    // Normalized before it reaches react-query's cache, exactly as in
    // `getDashboard` — this route serves the same stored spec, so it sees the
    // same v2/v3 mix until the backend's migration script runs in this env. A
    // normalizer failure rejects the query → the view's failed-to-load state.
    // Read off the RAW spec, before normalizing — see `getDashboard`.
    servedSchemaVersion: res.spec ? servedSchemaVersion(res.spec) : undefined,
    spec: res.spec
      ? normalizeSpecOrThrow(res.spec, { source: "fetchUseCaseDashboard", dashboardId })
      : undefined,
  };
};

// ── RBAC sharing (creator-gated in the UI) ───────────────────────────────────

/** Serializes a share request to the backend's snake_case body. */
const toShareBody = (body: ShareDashboardRequest) => ({
  company_id: body.companyId,
  scope: body.scope,
  role_id: body.roleId,
  user_id: body.userId,
});

/** `POST /dashboards/{id}/share` — grant a role/user access to a dashboard. */
export const shareDashboard = async (
  dashboardId: string,
  body: ShareDashboardRequest,
): Promise<void> => {
  // Suppress the global error toast: the Share dialog reports every failure inline
  // per recipient (with Retry) and captures to Sentry itself, so a stacked global
  // "Not Found"/5xx toast would just double-signal the same failure.
  await post<void>(API.DASHBOARD_SHARE(dashboardId), toShareBody(body), {
    skipGlobalErrorHandler: true,
  });
};

/** Raw (snake_case) share row — best-effort, since there's no documented GET. */
interface RawDashboardShare {
  scope: "role" | "user";
  company_id?: number;
  company_name?: string;
  role_id?: number;
  role_name?: string;
  name?: string;
  user_id?: number;
  email?: string;
  first_name?: string;
  last_name?: string;
  created_by_user_id?: number;
  created_by_name?: string;
  created_at?: string;
}

/**
 * `GET /dashboards/{id}/share` — the current shares for a dashboard ("Who has
 * access"). Lazy: only called when the disclosure is expanded. There is NO
 * documented GET on this path (POST/DELETE are the documented verbs), so this
 * tolerates a 404 (the caller renders a calm "not available yet" state) and maps
 * whatever list-shaped payload comes back. Accepts either a bare array or a
 * `{data|shares}` wrapper.
 */
export const getDashboardShares = async (
  dashboardId: string,
): Promise<DashboardShareEntry[]> => {
  // No documented GET here — a 404 is expected on backends that haven't shipped
  // it, and the share path answers the GET verb with 405 (Method Not Allowed).
  // Skip the global toast for both; the disclosure renders a calm "not available
  // yet" state from the query error instead.
  const res = await get<RawDashboardShare[] | { data?: RawDashboardShare[]; shares?: RawDashboardShare[] }>(
    API.DASHBOARD_SHARE(dashboardId),
    { skipGlobalErrorHandler: [404, 405] },
  );
  const rows = Array.isArray(res) ? res : res?.data ?? res?.shares ?? [];
  return rows.map((r) => ({
    scope: r.scope,
    companyId: r.company_id,
    companyName: r.company_name || undefined,
    roleId: r.role_id,
    roleName: r.role_name || r.name,
    userId: r.user_id,
    email: r.email,
    firstName: r.first_name,
    lastName: r.last_name,
    createdByUserId: r.created_by_user_id,
    createdByName: r.created_by_name || undefined,
    createdAt: r.created_at || undefined,
  }));
};

/** A use case a dashboard is assigned to — the minimal shape the card/list badges need. */
export interface DashboardUseCaseTag {
  id: number;
  name: string;
}

/** Raw (snake_case) reverse-assignment row from `GET /dashboards/use-case-assignments`. */
interface RawDashboardUseCaseAssignment {
  dashboard_id: string;
  use_cases?: { id: number; name: string }[];
}

/**
 * `GET /dashboards/use-case-assignments` → the reverse map dashboard_id → the use
 * cases it belongs to, so the Published landing can badge each dashboard without
 * an N+1 walk of the (admin-gated) catalog. Read-scoped + owner/company-scoped on
 * the backend (NOT admin-gated).
 *
 * This is a NEW endpoint: backends that haven't shipped it answer 404/405; a plain
 * creator on a stricter env can see a 403; and on dev the request falls through to
 * the `/dashboards/{dashboard_id:int}` route, which rejects the literal path segment
 * with a 422 ("not a valid integer"). All of these degrade to an empty map so the
 * landing simply renders no badges (never a broken/placeholder state).
 *
 * Badges are best-effort enrichment, so this fetch is FULLY SILENT:
 * `skipGlobalErrorHandler: true` suppresses the global error toast for EVERY status
 * (a status list would let 422/5xx leak a toast on the Published tab). A genuine 5xx
 * is still re-thrown so react-query records it (retry is off in the hook) — it just
 * never toasts.
 *
 * Returns a `Map<dashboardId, DashboardUseCaseTag[]>`; dashboards with no use case
 * are omitted from the map entirely.
 */
export const fetchDashboardUseCaseMap = async (): Promise<Map<string, DashboardUseCaseTag[]>> => {
  const map = new Map<string, DashboardUseCaseTag[]>();
  try {
    const res = await get<RawDashboardUseCaseAssignment[]>(API.DASHBOARD_USE_CASE_ASSIGNMENTS, {
      // Never toast — badges are optional enrichment. `true` covers all statuses,
      // including the dev-only 422 from the {dashboard_id:int} route collision.
      skipGlobalErrorHandler: true,
    });
    for (const row of res ?? []) {
      if (row?.dashboard_id == null) continue;
      const tags = (row.use_cases ?? [])
        .filter((u) => u?.id != null)
        .map((u) => ({ id: u.id, name: u.name }));
      if (tags.length) map.set(row.dashboard_id, tags);
    }
    return map;
  } catch (err) {
    // Endpoint absent (404/405), forbidden on a stricter env (403), or the dev
    // route-collision 422: degrade to no badges. A genuine 5xx still propagates so
    // it isn't silently swallowed (but it won't toast — see skipGlobalErrorHandler).
    const status = (err as IError)?.response?.status;
    if (status === 403 || status === 404 || status === 405 || status === 422) return map;
    throw err;
  }
};

/** `DELETE /dashboards/{id}/share` — revoke a previously granted share. */
export const revokeDashboardShare = async (
  dashboardId: string,
  body: ShareDashboardRequest,
): Promise<void> => {
  // axios `delete` carries the body under `config.data`.
  await del<void>(API.DASHBOARD_SHARE(dashboardId), { data: toShareBody(body) });
};

// ── Catalog + entitlements (SarasAdmin-gated on the backend) ──────────────────

/** `GET /use-cases/{id}/dashboards` → the dashboards assigned to a use case. */
export const fetchUseCaseDashboards = async (useCaseId: number): Promise<DashboardRef[]> => {
  const res = await get<RawDashboardRef[]>(API.USE_CASES.DASHBOARDS(useCaseId));
  return (res ?? []).map(toDashboardRef).sort((a, b) => a.orderIndex - b.orderIndex);
};

/**
 * `GET /use-cases` → the full use-case catalog (management view). The list route
 * returns flat `UseCaseResponse` rows (no dashboards); we fan out one
 * `GET /use-cases/{id}/dashboards` per use case so the management UI can show
 * each use case's assigned dashboards.
 */
export const fetchUseCases = async (): Promise<UseCase[]> => {
  const res = await get<RawUseCase[]>(API.USE_CASES.BASE);
  const bare = (res ?? []).map(toUseCase);
  return Promise.all(
    bare.map(async (uc) => ({ ...uc, dashboards: await fetchUseCaseDashboards(uc.id) })),
  );
};

/**
 * `POST /use-cases` → create a use case. The backend auto-assigns the integer
 * `id` (primary key); the client sends only the display `name` + optional
 * `description`; `enabled` defaults true.
 */
export const createUseCase = async (input: {
  name: string;
  description?: string;
}): Promise<UseCase> => {
  const res = await post<RawUseCase>(API.USE_CASES.BASE, {
    name: input.name,
    description: input.description,
    enabled: true,
  });
  return toUseCase(res);
};

/** `POST /use-cases/{id}/dashboards` → assign a dashboard to a use case (with order). */
export const assignDashboard = async (
  useCaseId: number,
  dashboardId: string,
  orderIndex?: number,
): Promise<void> => {
  await post<void>(API.USE_CASES.DASHBOARDS(useCaseId), {
    dashboard_id: dashboardId,
    order_index: orderIndex,
  });
};

/** `DELETE /use-cases/{id}/dashboards/{dashboardId}` → remove a dashboard from a use case. */
export const removeDashboard = async (useCaseId: number, dashboardId: string): Promise<void> => {
  await del<void>(API.USE_CASES.DASHBOARD_ASSIGN(useCaseId, dashboardId));
};

/**
 * Persist a new tab order for a use case's dashboards. There's no dedicated
 * reorder route: the assign `POST` is an upsert on `(use_case_id, dashboard_id)`,
 * so re-posting each already-assigned dashboard with its new position rewrites
 * `order_index`. Idempotent — safe to send the full ordered list every time.
 *
 * @param orderedDashboardIds - the dashboard ids in their new order; the array
 *   index becomes each dashboard's `order_index`.
 */
export const reorderUseCaseDashboards = async (
  useCaseId: number,
  orderedDashboardIds: string[],
): Promise<void> => {
  await Promise.all(
    orderedDashboardIds.map((dashboardId, i) => assignDashboard(useCaseId, dashboardId, i)),
  );
};

/**
 * `GET /use-cases/companies/{companyId}/entitlements` → a company's subscribed use
 * cases. The backend returns the full `UseCaseResponse` objects under
 * `entitlements`; the consumer only needs their (string) ids.
 *
 * @deprecated Backend PR sarasanalytics-com/iq-backend#923 made subscription-service
 * levers the sole source of use-case access; the `company_use_case_entitlements`
 * table this reads is no longer authoritative. Read entitlement from the levers
 * instead (`useCompanyEntitledUseCaseIds`).
 */
export const getEntitlements = async (companyId: number): Promise<CompanyEntitlements> => {
  const res = await get<RawCompanyEntitlements>(API.USE_CASES.ENTITLEMENTS(companyId));
  return { companyId: res.company_id, useCaseIds: (res.entitlements ?? []).map((e) => e.id) };
};

/**
 * `PUT /use-cases/companies/{companyId}/entitlements` → set a company's subscribed use cases.
 *
 * @deprecated Backend PR sarasanalytics-com/iq-backend#923 made subscription-service
 * levers the sole source of use-case access; the `company_use_case_entitlements`
 * table this writes is no longer read, so a successful write changes nothing.
 */
export const setEntitlements = async (
  companyId: number,
  useCaseIds: number[],
): Promise<CompanyEntitlements> => {
  const res = await put<RawCompanyEntitlements>(API.USE_CASES.ENTITLEMENTS(companyId), {
    use_case_ids: useCaseIds,
  });
  return { companyId: res.company_id, useCaseIds: (res.entitlements ?? []).map((e) => e.id) };
};

/**
 * `GET /use-cases/roles` → shareable roles for the share-modal picker. Returns a
 * bare `list[RoleResponse]` (`{role_id, name, role_name}`); the `id`/`name` shape
 * the picker consumes maps from `role_id` and the display `name` (falling back to
 * `role_name`).
 */
export const fetchRoles = async (): Promise<UseCaseRole[]> => {
  const res = await get<RawUseCaseRole[]>(API.USE_CASES.ROLES);
  return (res ?? []).map((r) => ({ id: r.role_id, name: r.name || r.role_name || "" }));
};
